import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Calendar, Check, Crown, TrendingUp } from "lucide-react";
import { useSuspenseGetSubscription } from "@/app/billing/api/hooks";
import { useScheduledChangeHelpers } from "@/app/billing/api/use-scheduled-change-helpers";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import type { Plan as PricingPlan } from "../plans";
import { getPlanColorClasses } from "../utils";
import { PricingCardCta } from "./pricing-card-cta";

export function PricingCard(
  plan: PricingPlan & {
    isCurrent?: boolean;
    isNext?: boolean;
  }
) {
  const {
    id,
    name,
    description,
    price,
    priceDetail,
    cta,
    ctaHref,
    features,
    color,
    buttonVariant,
    bgImage,
    customButtonClass,
    isMostPopular,
  } = plan;

  const { isCurrent = false, isNext = false } = plan;
  const colorClasses = getPlanColorClasses(color);

  const { organizationId } = useOrgProjectRouteContext();
  const { data: subscription } = useSuspenseGetSubscription(organizationId);
  const currentPlanId = subscription?.plan?.external_plan_id || "enterprise";

  const { nextScheduledChange, hasScheduledChangeTo, formatDate } =
    useScheduledChangeHelpers(organizationId);

  // Check if this plan has a scheduled change
  const hasScheduledChangeToThisPlan = hasScheduledChangeTo(id);

  const cardClasses = cn(
    "group relative flex flex-col gap-4 overflow-visible transition-colors",
    colorClasses.hoverRing,
    colorClasses.hoverBorder,
    isCurrent && "ring-2 ring-primary/60",
    (isNext || hasScheduledChangeToThisPlan) &&
      !isCurrent &&
      "ring-2 ring-border"
  );

  // Helper function to render status badges
  const renderStatusBadge = () => {
    const shouldShowBadge =
      (!isMostPopular &&
        (isCurrent || isNext || hasScheduledChangeToThisPlan)) ||
      (isMostPopular && isCurrent);

    if (!shouldShowBadge) {
      return null;
    }

    return (
      <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 transform">
        {isCurrent && !hasScheduledChangeToThisPlan && (
          <Badge className="bg-primary text-primary-foreground shadow-lg">
            <Crown className="h-3 w-3" />
            Current Plan
          </Badge>
        )}
        {!!(isCurrent && hasScheduledChangeToThisPlan) && (
          <Badge className="bg-primary text-primary-foreground shadow-lg">
            <Crown className="h-3 w-3" />
            Current Plan
          </Badge>
        )}
        {isNext && !isCurrent && !hasScheduledChangeToThisPlan && (
          <Badge
            className="border-muted-foreground bg-background text-muted-foreground shadow-lg"
            variant="outline"
          >
            <TrendingUp className="h-3 w-3" />
            Recommended
          </Badge>
        )}
        {hasScheduledChangeToThisPlan && !isCurrent && nextScheduledChange && (
          <Badge
            className="border-muted-foreground bg-background text-muted-foreground shadow-lg"
            variant="outline"
          >
            <Calendar className="h-3 w-3" />
            Scheduled {formatDate(nextScheduledChange.start_date)}
          </Badge>
        )}
      </div>
    );
  };

  // Helper function to render popular badge
  const renderPopularBadge = () => {
    if (!isMostPopular || currentPlanId === "enterprise" || isCurrent) {
      return null;
    }

    return (
      <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 transform">
        <Badge className="bg-red-500 px-4 py-1 font-medium text-white text-xs shadow-lg">
          MOST POPULAR
        </Badge>
      </div>
    );
  };

  return (
    <Card className={cardClasses}>
      {/* Most Popular badge */}
      {renderPopularBadge()}

      {/* Status badges */}
      {renderStatusBadge()}

      <CardHeader className="relative flex flex-col gap-1.5 pt-4 pb-0">
        <div className="flex min-h-[180px] flex-col">
          <div className="relative z-[2] flex w-full items-center justify-between">
            <CardTitle className="font-normal font-serif text-2xl leading-tight tracking-wide">
              {name}
            </CardTitle>
          </div>
          {!!description && (
            <CardDescription className="mt-1 mb-1 font-sans text-muted-foreground text-sm leading-6">
              {description}
            </CardDescription>
          )}
          <div className="mt-2 mb-4 flex items-center gap-1.5">
            <span className={cn("font-normal font-sans text-3xl leading-none")}>
              {price}
            </span>
            {!!priceDetail && (
              <span className="ml-2 align-middle text-muted-foreground text-xs uppercase tracking-wider">
                {priceDetail}
              </span>
            )}
          </div>
        </div>
        {/* CTA */}
        <PricingCardCta
          buttonVariant={buttonVariant}
          cta={cta}
          ctaHref={ctaHref}
          customButtonClass={customButtonClass}
          id={id}
          isCurrent={isCurrent}
          isNext={isNext}
        />

        {/* Decorative image sits behind other header content */}
        <div className="pointer-events-none absolute right-0 bottom-0 z-[1] h-24 w-24">
          <img
            alt=""
            className="h-full w-full object-contain opacity-0 transition-opacity md:opacity-30 lg:opacity-0 lg:group-hover:opacity-30"
            height={96}
            loading="lazy"
            src={bgImage}
            width={96}
          />
        </div>
      </CardHeader>
      <Separator className="my-3 w-full" />
      <CardContent className="z-10 flex flex-grow flex-col p-5 pt-0">
        {features.map((group, idx) => (
          <div key={`${name}-${group[0]}`}>
            {/* group[0] is unique per group */}
            {idx !== 0 && <Separator className="my-3 w-full" />}
            <div className="space-y-3">
              {group.map((feature) => {
                const isEverythingLine =
                  feature.includes("Everything in") && feature.endsWith(":");

                if (isEverythingLine) {
                  return (
                    <div
                      className="mb-1 text-muted-foreground text-sm"
                      key={feature}
                    >
                      {feature}
                    </div>
                  );
                }

                return (
                  <div className="flex items-start" key={feature}>
                    <Check className="mt-1 mr-2 size-4.5 flex-shrink-0 text-green-500" />
                    <span className="text-muted-foreground text-sm leading-tight">
                      {feature}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
